/**
 * useMicSilence (web) — le micro ouvert qui ne capte aucun son (`micSilence`), mesuré sur la piste que
 * la salle publie : un analyseur posé sur elle, sans seconde capture du micro (l'ancien diagnostic en
 * ouvrait une, et ne mesurait donc que panneau ouvert). Lu deux fois par seconde, en Dialogue, micro
 * ouvert ; rien d'autre ne l'écoute.
 *
 * Le live ne se redessine que quand le verdict bascule : les lectures restent dans l'effet, et l'analyse
 * vit le temps de la piste (une coupure du micro ou un passage en Concentré ne la refait pas).
 *
 * Ce qui ne se mesure pas, et ne dit donc rien :
 *  - la piste filtrée : la suppression des bruits de fond (Krisp, `noiseFilterApplied`) rend du silence
 *    exact dès que l'élève se tait — un silence qui ne prouve pas un micro coupé ;
 *  - l'analyse du son que le navigateur n'a pas encore laissé démarrer (avant un geste de la page) : elle
 *    ne rend que des zéros. Elle repart au premier toucher ;
 *  - une piste finie (le micro changé : LiveKit en relance une, que la mesure reprend).
 */
import { useEffect, useRef, useState } from "react"

import { noiseFilterApplied, TRACK_EVENTS } from "../room/useNoiseFilter.web"
import { initialMicSilence, micSilenceReducer, peakOf, type MicSilenceEvent } from "./micSilence"
import { type UseMicSilenceOptions } from "./useMicSilence"

export type { UseMicSilenceOptions } from "./useMicSilence"

/** Une lecture toutes les… */
export const MIC_SILENCE_READ_MS = 500

type AudioContextConstructor = new () => AudioContext

function audioContextConstructor(): AudioContextConstructor | null {
    if (typeof window === "undefined") return null
    const scope = window as unknown as {
        AudioContext?: AudioContextConstructor
        webkitAudioContext?: AudioContextConstructor
    }
    return scope.AudioContext ?? scope.webkitAudioContext ?? null
}

export function useMicSilence(options: UseMicSilenceOptions): boolean {
    const { track, active, telemetry } = options
    const [silent, setSilent] = useState(false)
    const activeRef = useRef(active)
    activeRef.current = active
    // La piste relancée ou son filtre changé : la mesure repart sur ce que la piste porte alors.
    const [version, setVersion] = useState(0)
    useEffect(() => {
        if (track == null) return undefined
        const changed = () => setVersion((current) => current + 1)
        for (const event of TRACK_EVENTS) track.on(event, changed)
        return () => {
            for (const event of TRACK_EVENTS) track.off(event, changed)
        }
    }, [track])
    const filtered = noiseFilterApplied(track)

    useEffect(() => {
        setSilent(false)
        const Context = audioContextConstructor()
        const media = track?.mediaStreamTrack
        if (filtered || media == null || Context == null) return undefined

        let context: AudioContext
        let source: MediaStreamAudioSourceNode
        let analyser: AnalyserNode
        try {
            context = new Context()
            source = context.createMediaStreamSource(new MediaStream([media]))
            analyser = context.createAnalyser()
            analyser.fftSize = 2048
            source.connect(analyser)
        } catch (cause) {
            telemetry.error("mic_silence", cause)
            return undefined
        }
        const samples = new Float32Array(analyser.fftSize)

        // L'état de la règle reste ici : seul le verdict qui bascule redessine le live.
        let state = initialMicSilence
        const apply = (event: MicSilenceEvent) => {
            const next = micSilenceReducer(state, event)
            if (next.silent !== state.silent) setSilent(next.silent)
            state = next
        }

        const resume = () => {
            context.resume().catch((cause: unknown) => telemetry.error("mic_silence_resume", cause))
        }
        const unlisten = () => {
            window.removeEventListener("pointerdown", resume)
            window.removeEventListener("keydown", resume)
        }
        if (context.state === "suspended") {
            window.addEventListener("pointerdown", resume)
            window.addEventListener("keydown", resume)
        }

        const timer = setInterval(() => {
            if (!activeRef.current) {
                apply({ type: "reset" })
                return
            }
            if (context.state !== "running" || media.readyState === "ended") {
                apply({ type: "read", at: Date.now(), peak: null })
                return
            }
            unlisten()
            analyser.getFloatTimeDomainData(samples)
            apply({ type: "read", at: Date.now(), peak: peakOf(samples) })
        }, MIC_SILENCE_READ_MS)

        return () => {
            clearInterval(timer)
            unlisten()
            source.disconnect()
            context.close().catch((cause: unknown) => telemetry.error("mic_silence_close", cause))
        }
        // `version` : la piste relancée ou son filtre changé, sans que l'objet de la piste change.
    }, [track, filtered, version, telemetry])

    const muted = active && silent
    useEffect(() => {
        if (muted) telemetry.transition("mic_heard", "mic_silent", "silence")
    }, [muted, telemetry])

    return muted
}
